import type {
  Conflito,
  GravidadeDeConflito,
  Intervalo,
  SituacaoDeExcecao,
  TipoDeConflito,
} from '@/features/changes/tipos';

/**
 * A colisão vista do lado do Comitê de Mudanças.
 *
 * O servidor calcula o conflito **por mudança**: cada `GET /tickets/:id/change`
 * devolve a lista do ponto de vista daquela mudança. Numa reunião de CAB isso é
 * a informação errada — ninguém abre catorze fichas para descobrir que duas
 * delas brigam entre si. O que se olha é o par: *estas duas mexem no mesmo
 * servidor na mesma noite*.
 *
 * Este módulo faz a virada. Recebe as listas de cada mudança e devolve:
 *
 *   • **colisões**, uma por par de mudanças, sem repetição — a mesma briga
 *     aparece nas duas listas de origem, e mostrá-la duas vezes faria a reunião
 *     contar o dobro de problemas;
 *   • **bloqueios**, um por mudança que caiu dentro de um congelamento — este
 *     não é par, é uma mudança contra uma decisão que alguém já tomou.
 *
 * Módulo puro: recebe listas, devolve listas. Sem React, sem relógio.
 */

export interface ReferenciaDeMudanca {
  readonly ticketId: string;
  readonly numero: string;
  readonly assunto: string;
}

/** Uma mudança agendada, com o que o servidor detectou para ela. */
export interface MudancaAvaliada extends ReferenciaDeMudanca {
  readonly janelaId: string | null;
  readonly janelaNome: string | null;
  readonly conflitos: readonly Conflito[];
}

/** Duas mudanças que disputam o mesmo recurso em janelas que se sobrepõem. */
export interface Colisao {
  readonly chave: string;
  readonly tipo: TipoDeConflito;
  readonly gravidade: GravidadeDeConflito;
  readonly a: ReferenciaDeMudanca;
  readonly b: ReferenciaDeMudanca;
  /** Ids dos recursos compartilhados — item de configuração ou serviço. */
  readonly recursos: readonly string[];
  readonly sobreposicao: Intervalo;
  readonly mensagem: string;
}

/** Uma mudança dentro de um período de congelamento. */
export interface Bloqueio {
  readonly chave: string;
  readonly mudanca: ReferenciaDeMudanca;
  readonly janelaNome: string | null;
  readonly blackoutId: string | null;
  readonly gravidade: GravidadeDeConflito;
  readonly sobreposicao: Intervalo;
  readonly mensagem: string;
}

export interface PanoramaDeConflitos {
  readonly colisoes: Colisao[];
  readonly bloqueios: Bloqueio[];
}

/**
 * A referência da outra ponta.
 *
 * Quando a outra mudança não está no período visível — ela cai numa janela que
 * só começa em novembro, por exemplo —, o dicionário não a tem. Devolver o id
 * cru é honesto: a tela mostra "mudança fora do período" com o identificador,
 * em vez de inventar um número que não existe.
 */
function referenciaDe(
  ticketId: string,
  conhecidas: ReadonlyMap<string, ReferenciaDeMudanca>,
): ReferenciaDeMudanca {
  return conhecidas.get(ticketId) ?? { ticketId, numero: '—', assunto: 'Mudança fora do período' };
}

/**
 * O par, sem depender de quem foi carregado primeiro.
 *
 * A chave ordena os dois ids: `A|B` e `B|A` são a mesma briga, e sem a ordem a
 * deduplicação dependeria da ordem de chegada das requisições — que muda a cada
 * carregamento.
 */
function chaveDoPar(x: string, y: string, tipo: string): string {
  return x < y ? `${x}|${y}|${tipo}` : `${y}|${x}|${tipo}`;
}

/** Bloqueante primeiro; depois o que dura mais tempo sobreposto. */
function porGravidade(
  a: { gravidade: GravidadeDeConflito; sobreposicao: Intervalo },
  b: { gravidade: GravidadeDeConflito; sobreposicao: Intervalo },
): number {
  const peso = Number(b.gravidade === 'BLOQUEANTE') - Number(a.gravidade === 'BLOQUEANTE');
  if (peso !== 0) return peso;
  return (
    b.sobreposicao.fim - b.sobreposicao.inicio - (a.sobreposicao.fim - a.sobreposicao.inicio)
  );
}

export function reunirConflitos(mudancas: readonly MudancaAvaliada[]): PanoramaDeConflitos {
  const conhecidas = new Map<string, ReferenciaDeMudanca>(
    mudancas.map((m) => [m.ticketId, { ticketId: m.ticketId, numero: m.numero, assunto: m.assunto }]),
  );

  const colisoes = new Map<string, Colisao>();
  const bloqueios = new Map<string, Bloqueio>();

  for (const mudanca of mudancas) {
    for (const conflito of mudanca.conflitos) {
      if (conflito.tipo === 'CONGELAMENTO') {
        // Um congelamento é do par (mudança, congelamento): a mesma mudança
        // pode cair em dois congelamentos diferentes, e os dois interessam.
        const chave = `${mudanca.ticketId}|${conflito.comBlackoutId ?? 'sem-id'}`;
        bloqueios.set(chave, {
          chave,
          mudanca: referenciaDe(mudanca.ticketId, conhecidas),
          janelaNome: mudanca.janelaNome,
          blackoutId: conflito.comBlackoutId ?? null,
          gravidade: conflito.gravidade,
          sobreposicao: conflito.sobreposicao,
          mensagem: conflito.mensagem,
        });
        continue;
      }

      const outro = conflito.comTicketId;
      if (outro === undefined) continue;

      const chave = chaveDoPar(mudanca.ticketId, outro, conflito.tipo);
      const existente = colisoes.get(chave);
      if (existente !== undefined) {
        // A outra ponta já registrou o par. O que ela pode acrescentar é
        // recurso: as duas listas nomeiam os mesmos ids, mas uma delas pode ter
        // sido carregada com o escopo já atualizado.
        const recursos = new Set([...existente.recursos, ...conflito.recursos]);
        colisoes.set(chave, { ...existente, recursos: [...recursos] });
        continue;
      }

      const a = referenciaDe(mudanca.ticketId, conhecidas);
      const b = referenciaDe(outro, conhecidas);
      colisoes.set(chave, {
        chave,
        tipo: conflito.tipo,
        gravidade: conflito.gravidade,
        // Ordena o par pelo número para que a mesma briga se leia igual toda
        // vez, independentemente de qual das duas foi carregada antes.
        ...(a.numero <= b.numero ? { a, b } : { a: b, b: a }),
        recursos: [...conflito.recursos],
        sobreposicao: conflito.sobreposicao,
        mensagem: conflito.mensagem,
      });
    }
  }

  return {
    colisoes: [...colisoes.values()].sort(porGravidade),
    bloqueios: [...bloqueios.values()].sort(porGravidade),
  };
}

/**
 * Quantos conflitos cada mudança tem, para a etiqueta da janela no calendário.
 *
 * Conta a partir do panorama já deduplicado, e não das listas cruas: uma
 * mudança que aparece nos dois lados de uma colisão tem **um** conflito, não
 * dois.
 */
export function contarPorMudanca(panorama: PanoramaDeConflitos): Map<string, number> {
  const contagem = new Map<string, number>();
  const somar = (ticketId: string) => contagem.set(ticketId, (contagem.get(ticketId) ?? 0) + 1);
  for (const colisao of panorama.colisoes) {
    somar(colisao.a.ticketId);
    somar(colisao.b.ticketId);
  }
  for (const bloqueio of panorama.bloqueios) somar(bloqueio.mudanca.ticketId);
  return contagem;
}

/** Os ids de mudança que estão travados — o que impede o agendamento. */
export function mudancasBloqueadas(panorama: PanoramaDeConflitos): Set<string> {
  return new Set(
    panorama.bloqueios
      .filter((bloqueio) => bloqueio.gravidade === 'BLOQUEANTE')
      .map((bloqueio) => bloqueio.mudanca.ticketId),
  );
}

// ── congelamento que barra o agendamento — critério 4.12 ───────────────────

/**
 * Os congelamentos que barram esta mudança, olhando as duas origens.
 *
 * ★ São **duas**, e é aí que estava o furo: `GET /tickets/:id/change` calcula a
 * colisão da janela em que a mudança já está, então uma mudança ainda sem
 * janela devolve `blocking: []` sempre. No fluxo normal — escolher no combo
 * uma janela que cai em congelamento — o único lugar em que o bloqueio existe
 * é `GET …/schedule/preview`, que o servidor já respondeu para a tela poder
 * desabilitar o botão. Ignorar essa lista fazia o "Pedir exceção" não
 * renderizar exatamente no caso em que ele é a única saída.
 *
 * Devolve os ids sem repetição e na ordem em que apareceram: o mesmo
 * congelamento pode vir das duas listas quando se reagenda de uma janela
 * congelada para outra.
 */
export function congelamentosBloqueando(
  ...listas: ReadonlyArray<readonly Conflito[]>
): string[] {
  const ids: string[] = [];
  for (const lista of listas) {
    for (const conflito of lista) {
      if (conflito.tipo !== 'CONGELAMENTO') continue;
      const id = conflito.comBlackoutId;
      if (id === undefined || ids.includes(id)) continue;
      ids.push(id);
    }
  }
  return ids;
}

/** Um pedido de exceção já registrado, do ponto de vista desta conta. */
export interface PedidoDeExcecao {
  readonly blackoutPeriodId: string;
  readonly status: SituacaoDeExcecao;
}

/**
 * Aqueles para os quais ainda falta pedir exceção.
 *
 * Pedido recusado **não** conta como pedido: recusar uma exceção e deixar a
 * mudança sem caminho nenhum seria transformar a recusa em beco sem saída. O
 * retirado idem — quem retirou pode ter retirado para reescrever a
 * justificativa.
 */
export function congelamentosSemExcecao(
  bloqueando: readonly string[],
  pedidas: readonly PedidoDeExcecao[],
): string[] {
  const vivo = (status: SituacaoDeExcecao) => status !== 'REJECTED' && status !== 'WITHDRAWN';
  return bloqueando.filter(
    (id) => !pedidas.some((pedido) => pedido.blackoutPeriodId === id && vivo(pedido.status)),
  );
}
